import { useState, useEffect } from 'react';
import axios from 'axios';
import { getValidAccessToken } from '../utils/tokenUtils';

const apiUrl = process.env.REACT_APP_API_URL;

/**
 * Custom hook to fetch live data from the backend
 * Handles real-time dashboard statistics based on user role
 * 
 * @param {number} refreshInterval - Interval in milliseconds to refresh data (default: 30000ms = 30s)
 * @returns {Object} - { liveData, loading, error, refresh }
 */
export const useLiveData = (refreshInterval = 30000) => {
  const [liveData, setLiveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLiveData = async () => {
    try {
      setLoading(true);
      const token = await getValidAccessToken();
      
      if (!token) {
        setError('No authentication token available');
        setLoading(false);
        return;
      }

      const response = await axios.get(`${apiUrl}/live-data/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setLiveData(response.data.data);
        setError(null);
      } else {
        setError(response.data.error || 'Failed to fetch live data');
      }
    } catch (err) {
      console.error('Error fetching live data:', err);
      setError(err.message || 'Error fetching live data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial fetch
    fetchLiveData();

    // Set up interval for periodic updates
    const interval = setInterval(fetchLiveData, refreshInterval);

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, [refreshInterval]);

  return {
    liveData,
    loading,
    error,
    refresh: fetchLiveData
  };
};

export default useLiveData;
